/**
 * KAN-722: небольшая форма префикса метки (код, коды телефонов через запятую,
 * описание). Общая для кнопки "+" в мастере подключения и блока "Метки".
 * Без prefix - создание, с prefix - изменение. Ошибка сервера (400 detail)
 * показывается под полями.
 */
import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useTelegramLabelPrefixMutations } from '@/hooks/useTelegramLabelPrefixes';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { parsePhonePrefixes } from '@/lib/telegramConnect';
import type { TelegramLabelPrefix } from '@/types/telegramLabelPrefix';

interface Props {
  /** Префикс id и data-testid полей и кнопок. */
  idPrefix: string;
  prefix?: TelegramLabelPrefix | null;
  onSaved: (prefix: TelegramLabelPrefix) => void;
  onCancel: () => void;
}

export function LabelPrefixForm({ idPrefix, prefix, onSaved, onCancel }: Props): JSX.Element {
  const { create, update } = useTelegramLabelPrefixMutations();
  const [code, setCode] = useState(prefix?.code ?? '');
  const [phones, setPhones] = useState((prefix?.phone_prefixes ?? []).join(', '));
  const [description, setDescription] = useState(prefix?.description ?? '');
  const [error, setError] = useState<string | null>(null);
  const pending = create.isPending || update.isPending;

  const submit = (): void => {
    const trimmed = code.trim();
    if (!trimmed) {
      setError('Введите код префикса');
      return;
    }
    setError(null);
    const data = {
      code: trimmed,
      phone_prefixes: parsePhonePrefixes(phones),
      description: description.trim(),
    };
    const handlers = {
      onSuccess: (saved: TelegramLabelPrefix) => onSaved(saved),
      onError: (err: unknown) =>
        setError(extractErrorMessage(err, 'Не удалось сохранить префикс')),
    };
    if (prefix) update.mutate({ id: prefix.id, data }, handlers);
    else create.mutate(data, handlers);
  };

  return (
    <div className="space-y-2 rounded-md border bg-gray-50 p-3" data-testid={`${idPrefix}-form`}>
      <div className="grid gap-2 sm:grid-cols-3">
        <div className="space-y-1">
          <Label htmlFor={`${idPrefix}-code`}>Код</Label>
          <Input
            id={`${idPrefix}-code`}
            data-testid={`${idPrefix}-code`}
            value={code}
            autoComplete="off"
            placeholder="UZ"
            disabled={pending}
            onChange={(e) => setCode(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor={`${idPrefix}-phones`}>Коды телефонов</Label>
          <Input
            id={`${idPrefix}-phones`}
            data-testid={`${idPrefix}-phones`}
            value={phones}
            autoComplete="off"
            placeholder="998, 992"
            disabled={pending}
            onChange={(e) => setPhones(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor={`${idPrefix}-description`}>Описание</Label>
          <Input
            id={`${idPrefix}-description`}
            data-testid={`${idPrefix}-description`}
            value={description}
            autoComplete="off"
            disabled={pending}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
      </div>
      <p className="text-xs text-gray-500">
        Коды телефонов через запятую: по ним префикс подбирается автоматически.
      </p>
      {error && (
        <p className="text-xs text-red-600" data-testid={`${idPrefix}-error`}>
          {error}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid={`${idPrefix}-cancel`}
          disabled={pending}
          onClick={onCancel}
        >
          Отмена
        </Button>
        <Button
          type="button"
          size="sm"
          data-testid={`${idPrefix}-save`}
          disabled={pending}
          onClick={submit}
        >
          {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {prefix ? 'Сохранить' : 'Добавить'}
        </Button>
      </div>
    </div>
  );
}

export default LabelPrefixForm;
